import { useState, useRef } from "react";
import {
  TextField,
  MenuItem,
  IconButton,
  InputAdornment,
  CircularProgress,
  Box,
  Divider,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";

export interface CreatableSelectOption {
  value: string;
  label: string;
}

interface CreatableSelectProps {
  label: string;
  value: string;
  options: CreatableSelectOption[];
  onChange: (value: string) => void;
  onCreate: (name: string) => Promise<string>;
  addLabel: string;
  createPlaceholder: string;
  createFailedMessage: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  error?: boolean;
}

export default function CreatableSelect({
  label,
  value,
  options,
  onChange,
  onCreate,
  addLabel,
  createPlaceholder,
  createFailedMessage,
  disabled = false,
  required = false,
  helperText,
  error: fieldError = false,
}: CreatableSelectProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const preventCloseRef = useRef(false);

  const handleSave = async () => {
    const trimmedName = newName.trim();
    if (!trimmedName || saving) return;

    setSaving(true);
    setError(null);

    try {
      onChange(await onCreate(trimmedName));
      setCreating(false);
      setOpen(false);
      setNewName("");
    } catch (err: any) {
      setError(err.message || createFailedMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setCreating(false);
    setOpen(false);
    setNewName("");
    setError(null);
  };

  return (
    <TextField
      label={label}
      select
      value={value}
      onChange={(e) => {
        const val = e.target.value;
        if (val === "__add_new__") {
          preventCloseRef.current = true;
          setCreating(true);
        } else {
          preventCloseRef.current = false;
          setCreating(false);
          setOpen(false);
          setNewName("");
          setError(null);
          onChange(val);
        }
      }}
      fullWidth
      required={required}
      disabled={disabled}
      size="small"
      helperText={helperText}
      error={fieldError}
      slotProps={{
        select: {
          open,
          onOpen: () => setOpen(true),
          onClose: () => {
            if (preventCloseRef.current) {
              preventCloseRef.current = false;
              return;
            }
            setCreating(false);
            setOpen(false);
            setNewName("");
            setError(null);
          },
        },
      }}
    >
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
      <Divider sx={{ my: 0.5 }} />
      {creating ? (
        <Box
          component="li"
          sx={{ px: 2, py: 1 }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <TextField
            value={newName}
            onChange={(e) => {
              setNewName(e.target.value);
              setError(null);
            }}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === "Enter" && newName.trim() && !saving) {
                handleSave();
              }
              if (e.key === "Escape") {
                handleCancel();
              }
            }}
            fullWidth
            disabled={saving}
            size="small"
            autoFocus
            placeholder={createPlaceholder}
            error={!!error}
            helperText={error}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSave}
                      disabled={!newName.trim() || saving}
                      size="small"
                      color="primary"
                    >
                      {saving ? (
                        <CircularProgress size={18} />
                      ) : (
                        <CheckIcon fontSize="small" />
                      )}
                    </IconButton>
                    <IconButton
                      onClick={handleCancel}
                      disabled={saving}
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Box>
      ) : (
        <MenuItem value="__add_new__">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              color: "primary.main",
            }}
          >
            <AddIcon fontSize="small" />
            {addLabel}
          </Box>
        </MenuItem>
      )}
    </TextField>
  );
}
